// Client-side netcode profiler. Collects dense per-tick metrics and sparse
// event streams into in-memory ring buffers during a recording session.
// Designed to be O(1) to call at hot-path sites, and a zero-cost no-op when
// `recording` is false (call sites should still `if (profiler.isRecording())`
// guard parameter-prep work that isn't already in hand).
//
// Invariants:
//  * No allocations in record* methods during steady-state. `push` on an
//    Array may amortise-grow the backing store — acceptable; recorders do
//    not need to be zero-GC, only bounded.
//  * All `wallTimeMs` values are `performance.now() - t0`, so client and
//    server profiles each use their own monotonic clocks. Cross-referencing
//    is done via `serverTick` (authoritative axis), not wall time.

import {
  BONES_PER_CHAR, FLOATS_PER_BONE,
  type ClientProfile, type ClientHealthSample,
  type ClientReconcileEvent,
  makeEmptyClientProfile, makeEmptyRemoteSlice
} from './types';

export interface ClientProfilerStartOpts {
  sessionId: string;
  localCharId: number;
}

export class NetcodeProfiler {
  private recording = false;
  private t0 = 0;
  private sessionId = '';
  private localCharId = -1;
  private profile: ClientProfile = makeEmptyClientProfile();
  private lastPatchMs = -1;
  private lastBonesMs = -1;
  // Cached last few tick times for rolling FPS / tick-per-sec computation.
  private recentTickMs: number[] = [];
  private recentFrameMs: number[] = [];
  private recentRollbackMs: number[] = [];

  isRecording(): boolean { return this.recording; }
  getSessionId(): string { return this.sessionId; }
  getLocalCharId(): number { return this.localCharId; }
  getProfile(): ClientProfile { return this.profile; }
  getDurationMs(): number {
    return this.recording ? (performance.now() - this.t0) : 0;
  }
  getStartWallMs(): number { return this.t0; }

  start(opts: ClientProfilerStartOpts): void {
    this.recording = true;
    this.t0 = performance.now();
    this.sessionId = opts.sessionId;
    this.localCharId = opts.localCharId;
    this.profile = makeEmptyClientProfile();
    this.lastPatchMs = -1;
    this.lastBonesMs = -1;
    this.recentTickMs.length = 0;
    this.recentFrameMs.length = 0;
    this.recentRollbackMs.length = 0;
  }

  stop(): void {
    this.recording = false;
  }

  now(): number {
    return performance.now() - this.t0;
  }

  // ------------------------------------------------------------------------
  // rAF frame

  recordRafFrame(args: {
    idx: number;
    realDtMs: number;
    substeps: number;
    accPreMs: number;
    accPostMs: number;
    alpha: number;
    frameDurationMs: number;
  }): void {
    if (!this.recording) return;
    const t = this.now();
    const f = this.profile.rafFrames;
    f.idx.push(args.idx);
    f.wallTimeMs.push(t);
    f.realDtMs.push(args.realDtMs);
    f.simSubstepsThisFrame.push(args.substeps);
    f.accumulatorPreMs.push(args.accPreMs);
    f.accumulatorPostMs.push(args.accPostMs);
    f.alpha.push(args.alpha);
    f.frameDurationMs.push(args.frameDurationMs);
    // Rolling FPS window (last 1s).
    this.recentFrameMs.push(t);
    while (this.recentFrameMs.length > 0 && this.recentFrameMs[0]! < t - 1000) {
      this.recentFrameMs.shift();
    }
  }

  // ------------------------------------------------------------------------
  // Sim tick

  recordSimTick(args: {
    clientTick: number;
    dtMs: number;
    tickDurationMs: number;
    stepDurationMs: number;
    synthDurationMs: number;
    syncLocalScalarDurationMs: number;
    capturePredictedDurationMs: number;
    reconcileDurationMs: number;
    ackTickObserved: number;
    localPelvisX: number;
    localPelvisY: number;
    localPelvisZ: number;
    localFacingYaw: number;
    localLinvelX: number;
    localLinvelY: number;
    localLinvelZ: number;
    localAngvelY: number;
    localHp: number;
    localKo: number;
    localStamina: number;
    localStunRemaining: number;
    localGrabbing: boolean;
    localBeingGrabbed: boolean;
    inputMoveX: number;
    inputMoveZ: number;
    inputAimYaw: number;
    inputButtons: number;
  }): void {
    if (!this.recording) return;
    const t = this.now();
    const s = this.profile.simTicks;
    s.clientTick.push(args.clientTick);
    s.wallTimeMs.push(t);
    s.dtMs.push(args.dtMs);
    s.tickDurationMs.push(args.tickDurationMs);
    s.stepDurationMs.push(args.stepDurationMs);
    s.synthDurationMs.push(args.synthDurationMs);
    s.syncLocalScalarDurationMs.push(args.syncLocalScalarDurationMs);
    s.capturePredictedDurationMs.push(args.capturePredictedDurationMs);
    s.reconcileDurationMs.push(args.reconcileDurationMs);
    s.ackTickObserved.push(args.ackTickObserved);
    s.localPelvisX.push(args.localPelvisX);
    s.localPelvisY.push(args.localPelvisY);
    s.localPelvisZ.push(args.localPelvisZ);
    s.localFacingYaw.push(args.localFacingYaw);
    s.localLinvelX.push(args.localLinvelX);
    s.localLinvelY.push(args.localLinvelY);
    s.localLinvelZ.push(args.localLinvelZ);
    s.localAngvelY.push(args.localAngvelY);
    s.localHp.push(args.localHp);
    s.localKo.push(args.localKo);
    s.localStamina.push(args.localStamina);
    s.localStunRemaining.push(args.localStunRemaining);
    s.localGrabbing.push(args.localGrabbing ? 1 : 0);
    s.localBeingGrabbed.push(args.localBeingGrabbed ? 1 : 0);
    s.inputMoveX.push(args.inputMoveX);
    s.inputMoveZ.push(args.inputMoveZ);
    s.inputAimYaw.push(args.inputAimYaw);
    s.inputButtons.push(args.inputButtons);
    // Rolling ticks/sec window.
    this.recentTickMs.push(t);
    while (this.recentTickMs.length > 0 && this.recentTickMs[0]! < t - 1000) {
      this.recentTickMs.shift();
    }
  }

  recordPdStateLocal(args: {
    clientTick: number;
    targetYaw: number;
    hipPlantX: number | null;
    hipPlantZ: number;
    gaitPhase: number;
    gaitActivity: number;
    jumpKickActive: number;
    punchCooldownL: number;
    punchCooldownR: number;
    swingPhaseL: number;
    swingPhaseR: number;
  }): void {
    if (!this.recording) return;
    const p = this.profile.pdStateLocal;
    p.clientTick.push(args.clientTick);
    p.targetYaw.push(args.targetYaw);
    p.hipPlantX.push(args.hipPlantX ?? NaN);
    p.hipPlantZ.push(args.hipPlantZ);
    p.gaitPhase.push(args.gaitPhase);
    p.gaitActivity.push(args.gaitActivity);
    p.jumpKickActive.push(args.jumpKickActive);
    p.punchCooldownL.push(args.punchCooldownL);
    p.punchCooldownR.push(args.punchCooldownR);
    p.swingPhaseL.push(args.swingPhaseL);
    p.swingPhaseR.push(args.swingPhaseR);
  }

  /** `bones` is 7 floats/bone × BONES_PER_CHAR (render format). Append to
   *  the flat local-bone-pose array. Call once per sim tick for the local
   *  character. */
  recordLocalBones(bones: Float32Array): void {
    if (!this.recording) return;
    const n = BONES_PER_CHAR * FLOATS_PER_BONE;
    if (bones.length !== n) return;
    const arr = this.profile.localBonePoses;
    for (let i = 0; i < n; i++) arr.push(bones[i]!);
  }

  recordRemoteTick(args: {
    charId: number;
    clientTick: number;
    serverTickApprox: number;
    pelvisX: number;
    pelvisY: number;
    pelvisZ: number;
    facingYaw: number;
    inputQueueDepthBefore: number;
    inputSource: -1 | 0 | 1;
    inputConsumedTick: number;
    inputMoveX: number;
    inputMoveZ: number;
    inputAimYaw: number;
    inputButtons: number;
  }): void {
    if (!this.recording) return;
    let slice = this.profile.remoteChars[args.charId];
    if (!slice) {
      slice = makeEmptyRemoteSlice();
      this.profile.remoteChars[args.charId] = slice;
    }
    slice.clientTick.push(args.clientTick);
    slice.serverTickApprox.push(args.serverTickApprox);
    slice.pelvisX.push(args.pelvisX);
    slice.pelvisY.push(args.pelvisY);
    slice.pelvisZ.push(args.pelvisZ);
    slice.facingYaw.push(args.facingYaw);
    slice.inputQueueDepthBefore.push(args.inputQueueDepthBefore);
    slice.inputSource.push(args.inputSource);
    slice.inputConsumedTick.push(args.inputConsumedTick);
    slice.inputMoveX.push(args.inputMoveX);
    slice.inputMoveZ.push(args.inputMoveZ);
    slice.inputAimYaw.push(args.inputAimYaw);
    slice.inputButtons.push(args.inputButtons);
  }

  // ------------------------------------------------------------------------
  // Network

  recordPatch(args: {
    serverTick: number;
    estimatedBytes: number;
    playerCount: number;
  }): void {
    if (!this.recording) return;
    const t = this.now();
    const p = this.profile.patches;
    p.wallTimeMs.push(t);
    p.serverTick.push(args.serverTick);
    p.interArrivalMs.push(this.lastPatchMs < 0 ? 0 : t - this.lastPatchMs);
    p.estimatedBytes.push(args.estimatedBytes);
    p.playerCount.push(args.playerCount);
    this.lastPatchMs = t;
  }

  recordBonesMessage(args: {
    serverTickApprox: number;
    sizeBytes: number;
    charsIncluded: number;
  }): void {
    if (!this.recording) return;
    const t = this.now();
    const b = this.profile.bonesMessages;
    b.wallTimeMs.push(t);
    b.serverTickApprox.push(args.serverTickApprox);
    b.sizeBytes.push(args.sizeBytes);
    b.interArrivalMs.push(this.lastBonesMs < 0 ? 0 : t - this.lastBonesMs);
    b.charsIncluded.push(args.charsIncluded);
    this.lastBonesMs = t;
  }

  recordReconcile(evt: ClientReconcileEvent): void {
    if (!this.recording) return;
    evt.wallTimeMs = this.now();
    this.profile.reconciles.push(evt);
    if (evt.action === 'rollback') {
      this.recentRollbackMs.push(evt.wallTimeMs);
      while (this.recentRollbackMs.length > 0 && this.recentRollbackMs[0]! < evt.wallTimeMs - 1000) {
        this.recentRollbackMs.shift();
      }
    }
  }

  recordInputSend(args: { clientTick: number; bundleSize: number; estimatedBytes: number }): void {
    if (!this.recording) return;
    this.profile.inputSends.push({
      wallTimeMs: this.now(),
      clientTick: args.clientTick,
      bundleSize: args.bundleSize,
      estimatedBytes: args.estimatedBytes
    });
  }

  // ------------------------------------------------------------------------
  // Events

  recordImpact(args: { fromCharId: number; toCharId: number; dmg: number; posX: number; posY: number; posZ: number }): void {
    if (!this.recording) return;
    this.profile.impacts.push({
      wallTimeMs: this.now(),
      fromCharId: args.fromCharId,
      toCharId: args.toCharId,
      dmg: args.dmg,
      posX: args.posX, posY: args.posY, posZ: args.posZ
    });
  }

  recordFootstep(args: { charId: number; foot: 'L' | 'R'; posX: number; posY: number; posZ: number }): void {
    if (!this.recording) return;
    this.profile.footsteps.push({
      wallTimeMs: this.now(),
      charId: args.charId,
      foot: args.foot,
      posX: args.posX, posY: args.posY, posZ: args.posZ
    });
  }

  recordSwing(args: { charId: number; side: 'L' | 'R' }): void {
    if (!this.recording) return;
    this.profile.swings.push({
      wallTimeMs: this.now(),
      charId: args.charId,
      side: args.side
    });
  }

  recordHealthSample(args: Omit<ClientHealthSample, 'wallTimeMs'>): void {
    if (!this.recording) return;
    this.profile.health.push({
      ...args,
      wallTimeMs: this.now()
    });
  }

  setRollbackCountStart(count: number): void {
    if (!this.recording) return;
    this.profile.rollbackCountStart = count;
  }
  setRollbackCountEnd(count: number): void {
    this.profile.rollbackCountEnd = count;
  }

  // ------------------------------------------------------------------------
  // HUD helpers

  /** Rolling FPS over the last ~1s. Works even when not recording (reads
   *  the rolling window we keep anyway while `recording`). */
  currentFps(): number {
    if (this.recentFrameMs.length < 2) return 0;
    const span = this.recentFrameMs[this.recentFrameMs.length - 1]! - this.recentFrameMs[0]!;
    return span > 0 ? (this.recentFrameMs.length - 1) * 1000 / span : 0;
  }

  /** Rolling ticks-per-second over the last ~1s. */
  currentTickHz(): number {
    if (this.recentTickMs.length < 2) return 0;
    const span = this.recentTickMs[this.recentTickMs.length - 1]! - this.recentTickMs[0]!;
    return span > 0 ? (this.recentTickMs.length - 1) * 1000 / span : 0;
  }

  /** Rollbacks in the last ~1s (moving average). */
  currentRollbackHz(): number {
    return this.recentRollbackMs.length;
  }
}

/** Global singleton used by the main loop, transport, HUD. */
export const netcodeProfiler = new NetcodeProfiler();
